import { FigureShot } from './FigureShot'

interface Props { cs: boolean }

function CsBody() {
  return (
    <>
      <p>
        Po spuštění ShowX a otevření show dostaneš <strong>shell</strong> — Electron okno na FOH Macu. Shell je řídící
        centrum: vidíš v něm stav show, routing a zařízení a sleduješ dispatch log. PWA stanice (iPady, operátoři) se
        připojí přes LAN a stanou se aktivními místy pro GO.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Přehled shellu — záložky Show, Routing, Devices</h3>
      <p>
        Shell má horní panel se třemi záložkami. Pod panelem jsou vždy viditelné <strong>StationsPanel</strong> (připojené
        stanice) a <strong>DispatchLogPanel</strong> (live log vyslání payloadů).
      </p>
      <FigureShot
        src="/shots/prirucka/01_shell_overview.png"
        alt="ShowX shell — záložka Show s názvem show, mode badge a cuelist summary"
        caption="Shell · záložka Show · Firemní show 2026 · REHEARSAL mode"
      />

      <h3 className="display-serif text-xl mt-10 mb-3">Záložka Show</h3>
      <p>
        Záložka <strong>Show</strong> zobrazuje:
      </p>
      <ul>
        <li>
          <strong>Název show</strong> a datum — například "Firemní show 2026". Kliknutím na název ho edituješ.
        </li>
        <li>
          <strong>Mode badge</strong> — <code>REHEARSAL</code> nebo <code>SHOW</code>. Klik na badge přepíná mezi
          režimy (SM oprávnění). Detaily v kapitole 5.
        </li>
        <li>
          <strong>Cuelist summary</strong> — název cuelistu a počet cues. Jednotlivé cue řádky jsou v PWA stanicích
          (SM master view, operátor view) — ne v shellu.
        </li>
        <li>
          <strong>STATIONS panel</strong> — URL stanice, QR kód, seznam připojených stanic s rolemi.
        </li>
      </ul>

      <h3 className="display-serif text-xl mt-10 mb-3">Záložka Routing</h3>
      <p>
        Záložka <strong>Routing</strong> spravuje pravidla pro přesměrování payloadů na zařízení.
      </p>
      <FigureShot
        src="/shots/prirucka/03_shell_routing.png"
        alt="ShowX shell — záložka Routing s tabulkou pravidel (match → device)"
        caption="Shell · záložka Routing · pravidla pro OSC routing na ETC Eos, QLab, disguise"
      />
      <p>
        Každé pravidlo má:
      </p>
      <ul>
        <li><strong>Match</strong> — <code>payload_type</code> (např. <code>lx_ref</code>) nebo <code>tag_pattern</code> (např. <code>SX</code>).</li>
        <li><strong>Target device</strong> — ID zařízení, na které má payload jít.</li>
        <li><strong>Notes</strong> — volitelný popis (např. "LX payloady → ETC Eos").</li>
      </ul>
      <p>
        Pravidla se vyhodnocují shora. Prázdný match funguje jako fallback — chytí co nezachytilo nic jiného.
        Tlačítka <strong>+ Add rule</strong> / ikona koše pro odebrání.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Záložka Devices</h3>
      <p>
        Záložka <strong>Devices</strong> spravuje seznam konfigurovaných výstupních zařízení.
      </p>
      <FigureShot
        src="/shots/prirucka/04_shell_devices.png"
        alt="ShowX shell — záložka Devices s tabulkou zařízení (ETC Eos, QLab, disguise)"
        caption="Shell · záložka Devices · ETC Eos (LX console), QLab (SX playback), disguise (video server)"
      />
      <p>
        Každé zařízení má:
      </p>
      <ul>
        <li><strong>Label</strong> — zobrazovaný název (např. "ETC Eos (LX console)").</li>
        <li><strong>Transport</strong> — <code>osc</code> (nejčastější), <code>midi</code>, <code>dmx</code>.</li>
        <li><strong>Host / Port</strong> — LAN IP a port (pro OSC).</li>
        <li><strong>Driver</strong> — <code>eos</code>, <code>qlab</code>, <code>generic</code>. Driver určuje jak ShowX formátuje zprávy pro dané zařízení.</li>
      </ul>

      <h3 className="display-serif text-xl mt-10 mb-3">Dispatch Log</h3>
      <p>
        Pod hlavním panelem shellu je <strong>Dispatch Log</strong> — live seznam každého odeslaného payloadu. Ukazuje
        čas, cue label, payload typ a zařízení. Klíčový nástroj pro debugování: pokud dispatch proběhl, ale výstup
        nefunguje, problém je na straně zařízení nebo routingu.
      </p>
      <FigureShot
        src="/shots/prirucka/02_shell_dispatch_log.png"
        alt="ShowX shell — Dispatch Log panel s live zápisem dispatchovaných payloadů"
        caption="Shell · Dispatch Log · záznamy po GO (LX 01 — Otevření sálu)"
      />
      <p>
        Log je v paměti — po restartu shellu se vyprázdní. Pro trvalou historii slouží soubor{' '}
        <code>history.jsonl</code> uvnitř <code>.showx</code> balíku.
      </p>
    </>
  )
}

function EnBody() {
  return (
    <>
      <p>
        When you launch ShowX and open a show you get the <strong>shell</strong> — the Electron window on the FOH Mac.
        The shell is the control centre: you see show state, routing, devices, and the dispatch log. PWA stations (iPads,
        operators) connect over LAN and become the active GO points.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Shell overview — tabs Show, Routing, Devices</h3>
      <p>
        The shell has a top panel with three tabs. Below the panel the <strong>StationsPanel</strong> (connected stations)
        and the <strong>DispatchLogPanel</strong> (live payload dispatch feed) are always visible.
      </p>
      <FigureShot
        src="/shots/prirucka/01_shell_overview.png"
        alt="ShowX shell — Show tab with show name, mode badge and cuelist summary"
        caption="Shell · Show tab · Firemní show 2026 · REHEARSAL mode"
      />

      <h3 className="display-serif text-xl mt-10 mb-3">Show tab</h3>
      <p>The <strong>Show</strong> tab displays:</p>
      <ul>
        <li><strong>Show name</strong> and date — e.g. "Firemní show 2026". Click to edit.</li>
        <li><strong>Mode badge</strong> — <code>REHEARSAL</code> or <code>SHOW</code>. Click to toggle modes (SM authority). Details in chapter 5.</li>
        <li><strong>Cuelist summary</strong> — cuelist name and cue count. Individual cue rows live in the PWA stations (SM master view, operator view) — not in the shell.</li>
        <li><strong>STATIONS panel</strong> — station URL, QR code, list of connected stations with roles.</li>
      </ul>

      <h3 className="display-serif text-xl mt-10 mb-3">Routing tab</h3>
      <p>The <strong>Routing</strong> tab manages rules that direct payloads to devices.</p>
      <FigureShot
        src="/shots/prirucka/03_shell_routing.png"
        alt="ShowX shell — Routing tab with rules table (match → device)"
        caption="Shell · Routing tab · rules for OSC routing to ETC Eos, QLab, disguise"
      />
      <p>Each rule has:</p>
      <ul>
        <li><strong>Match</strong> — <code>payload_type</code> (e.g. <code>lx_ref</code>) or <code>tag_pattern</code> (e.g. <code>SX</code>).</li>
        <li><strong>Target device</strong> — ID of the device to route the payload to.</li>
        <li><strong>Notes</strong> — optional description (e.g. "LX payloads → ETC Eos").</li>
      </ul>
      <p>
        Rules are evaluated top-down. An empty match is a fallback — it catches anything not matched above.
        Use <strong>+ Add rule</strong> / trash icon to add or remove.
      </p>

      <h3 className="display-serif text-xl mt-10 mb-3">Devices tab</h3>
      <p>The <strong>Devices</strong> tab manages the list of configured output devices.</p>
      <FigureShot
        src="/shots/prirucka/04_shell_devices.png"
        alt="ShowX shell — Devices tab with device table (ETC Eos, QLab, disguise)"
        caption="Shell · Devices tab · ETC Eos (LX console), QLab (SX playback), disguise (video server)"
      />
      <p>Each device has:</p>
      <ul>
        <li><strong>Label</strong> — display name (e.g. "ETC Eos (LX console)").</li>
        <li><strong>Transport</strong> — <code>osc</code> (most common), <code>midi</code>, <code>dmx</code>.</li>
        <li><strong>Host / Port</strong> — LAN IP and port (for OSC).</li>
        <li><strong>Driver</strong> — <code>eos</code>, <code>qlab</code>, <code>generic</code>. The driver controls how ShowX formats messages for that device.</li>
      </ul>

      <h3 className="display-serif text-xl mt-10 mb-3">Dispatch Log</h3>
      <p>
        Below the shell panel is the <strong>Dispatch Log</strong> — a live list of every sent payload. It shows time,
        cue label, payload type, and device. Key debugging tool: if a dispatch went through but the output didn't
        respond, the problem is on the device or routing side.
      </p>
      <FigureShot
        src="/shots/prirucka/02_shell_dispatch_log.png"
        alt="ShowX shell — Dispatch Log panel with live dispatch entries"
        caption="Shell · Dispatch Log · entries after GO (LX 01 — Otevření sálu)"
      />
      <p>
        The log is in-memory — it clears when the shell restarts. For permanent history, use the{' '}
        <code>history.jsonl</code> file inside the <code>.showx</code> package.
      </p>
    </>
  )
}

export default function Chapter2({ cs }: Props) {
  return (
    <article id="kap-2" className="scroll-mt-24">
      <h2 className="display-serif text-display-2 leading-tight mb-8">
        {cs ? '2. Hlavní okno' : '2. Main window'}
      </h2>
      <div className="copy prose-guide max-w-none">
        {cs ? <CsBody /> : <EnBody />}
      </div>
    </article>
  )
}
